import { useCallback, useEffect } from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import RichText from "common/blocks/RichText";
import useModalContext from "common/providers/modal/useModalContext";
import { CollectionTransferDeliveryWidgetProps } from "features/vehicleDetails/components/WidgetsRow/components/CollectionTransferDeliveryWidget";
import TransferDeliveryRow from "features/vehicleDetails/components/WidgetsRow/components/CollectionTransferDeliveryWidget/TransferDeliveryRow";
import useClosestStoresContext from "features/vehicleDetails/providers/closestStores/useClosestStoresContext";
import { ONLINE_STORE_SITE_ID } from "lib/constants";
import formatDateTime from "utils/formatDateTime";
import formatPrice from "utils/formatPrice";

import { deliveryOrCollectionFormModal, storeDetailsModal } from "../..";
import Wrapper from "../../common/Wrapper";
import styles from "../DeliveryOrCollection.module.scss";

const cx = classNames.bind(styles);

const formatPostcode = (postcode: string) => {
    const parts = postcode.match(/^([A-Z]{1,2}\d{1,2}[A-Z]?)\s*(\d[A-Z]{2})$/);
    parts?.shift();
    return parts?.join(" ");
};

const DeliveryOrCollectionDetailedView = ({
    params,
}: {
    params: CollectionTransferDeliveryWidgetProps;
}) => {
    const {
        afterPhoneNumberText,
        freeText,
        homeDeliveryAvailableText,
        readyForCollectionText,
        readyForCollectionDateTime,
        storeDirectionUrl,
        detailedInfoFreeText,
        getDirectionButtonText,
        removePostcodeButtonText,
        seeStoreDetailsButtonText,
        homeDeliveryPrice,
        vehicleTransferAvailableText,
        showHomeDelivery,
    } = params;
    const { useSidebar } = useModalContext();
    const { openSidebar, openSecondSidebar, closeSecondSidebar } = useSidebar();

    const { closestStores, hasClosestStores, lastPostcode, reset, allStoresDetailsData } =
        useClosestStoresContext();

    const onRemovePostcodeHandler = () => {
        reset();
        closeSecondSidebar();
        openSidebar(deliveryOrCollectionFormModal, { ...params, lastPostcode: "" });
    };

    const onSeeStoreDetailsClick = useCallback(
        (siteId: number) => {
            const storeData = closestStores.find((store: any) => store.siteId === siteId);
            const onlineStoreData = allStoresDetailsData.find(
                (store: any) => store.siteId === ONLINE_STORE_SITE_ID
            );
            openSecondSidebar(storeDetailsModal(`Motorpoint ${storeData?.siteName}`), {
                storeData,
                onlineStoreData,
            });
        },
        [allStoresDetailsData, closestStores, openSecondSidebar]
    );

    useEffect(() => {
        // Manually update infinity number
        window.setTimeout(() => {
            window._its?.("findAndReplaceNumbers");
        }, 0);
    });

    return (
        <Wrapper className={cx("wrapper")}>
            <div className={cx("deliveryOrCollectionSidebar")}>
                <RichText className={cx("richTextNoMargin")} {...detailedInfoFreeText} />
                <div className={cx("postcodeBadge")}>
                    <span>
                        Your postcode <span>{formatPostcode(lastPostcode)}</span>
                    </span>
                    <Button
                        theme="textLink"
                        onClick={onRemovePostcodeHandler}
                        ariaLabel={removePostcodeButtonText}
                    >
                        {removePostcodeButtonText}
                    </Button>
                </div>
                <div className={cx("detailRows")}>
                    <TransferDeliveryRow
                        key="readyForCollection"
                        icon="PropertyLocation2"
                        price={freeText}
                        className={cx({ hasClosestStores }, "rowPadding")}
                    >
                        <RichText {...readyForCollectionText} />
                        <span className={cx("fromDate")}>{readyForCollectionDateTime}</span>
                        <div className={cx("buttonRow")}>
                            <Button
                                type="button"
                                theme="textLink"
                                href={storeDirectionUrl}
                                target="_blank"
                                ariaLabel={getDirectionButtonText}
                            >
                                {getDirectionButtonText}
                            </Button>
                        </div>
                    </TransferDeliveryRow>
                    <TransferDeliveryRow
                        key="transfer-to-store"
                        icon="PropertyLocation"
                        className={cx("rowPadding")}
                    >
                        <div className={cx("contentWrapper")}>
                            <p>{vehicleTransferAvailableText}</p>
                            <ul className={cx("stores")}>
                                {closestStores?.map((store: any) => (
                                    <li key={store.siteId}>
                                        <span className={cx("title")}>
                                            <span>
                                                {store.siteName} - {store.address.postcode}
                                            </span>
                                            <span className={cx("price")}>
                                                {formatPrice(store.bookingSlot.price, freeText)}
                                            </span>
                                        </span>
                                        <span className={cx("fromDate")}>
                                            from {formatDateTime(store.bookingSlot.date)}.
                                        </span>

                                        <div className={cx("buttonRow")}>
                                            <Button
                                                type="button"
                                                theme="textLink"
                                                onClick={() => onSeeStoreDetailsClick(store.siteId)}
                                                ariaLabel={seeStoreDetailsButtonText}
                                            >
                                                {seeStoreDetailsButtonText}
                                            </Button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </TransferDeliveryRow>
                    {showHomeDelivery && (
                        <TransferDeliveryRow
                            key="home-delivery"
                            icon="DeliveryVan"
                            price={homeDeliveryPrice}
                            className={cx("rowPadding")}
                        >
                            <p>
                                {homeDeliveryAvailableText}.
                                <br />
                                Call <DynamicPhoneNumber
                                    isLink
                                    className={cx("phoneNumber")}
                                />{" "}
                                {afterPhoneNumberText}
                            </p>
                        </TransferDeliveryRow>
                    )}
                </div>
            </div>
        </Wrapper>
    );
};

export default DeliveryOrCollectionDetailedView;
